/**
 * Gig list — painted from IndexedDB (local-first). Reads like a tour book:
 * CHRONOLOGICAL (gig header `date`, undated last — never opaque-id order) with
 * date · city on every row. Tap → gig detail.
 */
import type { ReactNode } from "react";
import { registry } from "@td/registry";
import { isItemDeleted } from "@td/sync-core";
import { t, tLabel, type Locale } from "../i18n.ts";
import type { GigRecord } from "../db/idb.ts";

/**
 * Display name for a gig. P1 defines no gig header field spec (the gig entity
 * lands with the full registry in P3) — take the obvious candidates from the
 * create_gig fields, fall back to the id (honest, greppable).
 */
export function gigDisplayName(gig: GigRecord): string {
  const fields = gig.state.gig?.fields ?? {};
  // venue/city rescue imported v1 gigs whose header never got a name — a raw
  // id on the tour book is exactly the noise ADR-014 bans (Eduardo, 2026-07-02).
  for (const key of ["name", "title", "venue", "city"]) {
    const v = fields[key];
    if (typeof v === "string" && v.trim() !== "") return v;
  }
  // A truly nameless v1 gig (name/venue/city all empty): its date is the only
  // human handle it has — never a raw gig_… id on a briefing (ADR-014).
  const d = typeof fields["date"] === "string" ? (fields["date"] as string) : "";
  if (DAY_RE.test(d)) return d;
  return gig.gig_id;
}

/** Gig header field, read tolerantly (header spec is gig-level, not a registry collection). */
function headerField(gig: GigRecord, key: string): string {
  const v = gig.state.gig?.fields[key];
  return typeof v === "string" ? v.trim() : "";
}

const DAY_RE = /^\d{4}-\d{2}-\d{2}/;

/** The gig's date (YYYY-MM-DD…) or "" — the same header field the server sorts share views by. */
export function gigDate(gig: GigRecord): string {
  const d = headerField(gig, "date");
  return DAY_RE.test(d) ? d : "";
}

/**
 * Chronological order (v1's list read chronologically; opaque-id order was the
 * 2026-07-02 review regression): dated gigs ascending by date, undated last,
 * ties by name then id (stable).
 */
export function compareGigs(a: GigRecord, b: GigRecord): number {
  const da = gigDate(a);
  const db = gigDate(b);
  if (da !== db) {
    if (da === "") return 1;
    if (db === "") return -1;
    return da < db ? -1 : 1;
  }
  const na = gigDisplayName(a);
  const nb = gigDisplayName(b);
  if (na !== nb) return na < nb ? -1 : 1;
  return a.gig_id < b.gig_id ? -1 : 1;
}

function formatGigDate(locale: Locale, day: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(day);
  if (m === null) return day;
  return new Intl.DateTimeFormat(locale === "fr" ? "fr-FR" : "en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC", // the day IS the label — no device-tz reinterpretation
  }).format(new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))));
}

